import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import { notificationService } from '../../services/notificationService';
import { supabase } from '../../lib/supabase';
import NotificationCard from './components/NotificationCard';

const NotificationsCenter = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState(searchParams?.get('type') || 'all');
  const [filterPriority, setFilterPriority] = useState(searchParams?.get('priority') || 'all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const breadcrumbItems = [
    { label: 'Home', href: '/admin-dashboard' },
    { label: 'Notifications Center' }
  ];

  useEffect(() => {
    loadNotifications();
  }, []);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase?.auth?.getUser();
      const userId = user?.id;
      const result = await notificationService?.getUserNotifications(userId);
      if (result?.data) {
        setNotifications(result?.data);
      }
    } catch (err) {
      console.error('Error loading notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationClick = (notification) => {
    const notificationType = notification?.type || notification?.category;
    const siteId = notification?.site_id;
    const documentId = notification?.document_id;
    const incidentId = notification?.incident_id;
    const checklistId = notification?.checklist_id;
    
    // Deep-link to specific document detail page
    if (documentId && siteId) {
      navigate(`/sites/${siteId}/documents/${documentId}`);
      return;
    }
    
    // Deep-link to site with specific tab and item highlighting
    if (siteId) {
      let targetTab = 'documents';
      let itemId = null;
      
      if (notificationType === 'incident' || notification?.title?.toLowerCase()?.includes('incident')) {
        targetTab = 'incidents';
        itemId = incidentId;
      } else if (notificationType === 'checklist' || notification?.title?.toLowerCase()?.includes('checklist') || notification?.title?.toLowerCase()?.includes('task')) {
        targetTab = 'daily-tasks';
        itemId = checklistId;
      } else if (notificationType === 'audit' || notification?.title?.toLowerCase()?.includes('audit')) {
        targetTab = 'audits';
      } else if (notificationType === 'drawing' || notification?.title?.toLowerCase()?.includes('drawing')) {
        targetTab = 'drawings';
      } else if (notificationType === 'document' || notification?.title?.toLowerCase()?.includes('document') || notification?.title?.toLowerCase()?.includes('expir')) {
        targetTab = 'documents';
      }
      
      const queryParams = new URLSearchParams({ tab: targetTab });
      if (itemId) {
        queryParams?.append('highlight', itemId);
      }
      
      navigate(`/sites/${siteId}?${queryParams?.toString()}`);
      return;
    }
  };

  const filteredNotifications = notifications?.filter(notification => {
    // Type filter
    if (filterType !== 'all') {
      const notificationType = notification?.type || notification?.category || '';
      const title = notification?.title?.toLowerCase() || '';
      
      switch (filterType) {
        case 'critical':
          if (notification?.priority !== 'critical') return false;
          break;
        case 'expiring-7':
          if (!title?.includes('expir') || notification?.priority !== 'critical') return false;
          break;
        case 'expiring-14':
          if (!title?.includes('expir') || notification?.priority !== 'high') return false;
          break;
        case 'missing':
          if (!title?.includes('missing')) return false;
          break;
        case 'incidents':
          if (notificationType !== 'incident' && !title?.includes('incident')) return false;
          break;
        case 'checklists':
          if (notificationType !== 'checklist' && !title?.includes('checklist') && !title?.includes('task')) return false;
          break;
        case 'documents':
          if (notificationType !== 'document' && !title?.includes('document')) return false;
          break;
        default:
          break;
      }
    }

    // Priority filter
    if (filterPriority !== 'all' && notification?.priority !== filterPriority) {
      return false;
    }

    // Status filter (read/unread)
    if (filterStatus !== 'all') {
      const isRead = notification?.is_read || false;
      if (filterStatus === 'read' && !isRead) return false;
      if (filterStatus === 'unread' && isRead) return false;
    }

    // Search query
    if (searchQuery) {
      const searchLower = searchQuery?.toLowerCase();
      const matchesTitle = notification?.title?.toLowerCase()?.includes(searchLower);
      const matchesMessage = notification?.message?.toLowerCase()?.includes(searchLower);
      if (!matchesTitle && !matchesMessage) return false;
    }

    return true;
  });

  const handleClearFilters = () => {
    setFilterType('all');
    setFilterPriority('all');
    setFilterStatus('all');
    setSearchQuery('');
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Notifications Center - SiteProof</title>
      </Helmet>
      <Header />
      <main className="pt-20 pb-8 px-4 lg:px-6 max-w-7xl mx-auto">
        <Breadcrumb items={breadcrumbItems} />
        
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6 gap-4 mt-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">Notifications Center</h1>
            <p className="text-sm text-muted-foreground">
              {filteredNotifications?.length} {filteredNotifications?.length === 1 ? 'notification' : 'notifications'} found
            </p>
          </div>
          <Button
            variant="outline"
            iconName="RefreshCw"
            onClick={loadNotifications}
            disabled={loading}
          >
            Refresh
          </Button>
        </div>

        <div className="bg-card rounded-lg shadow-elevation-2 border border-border p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Filter by Type</label>
              <Select
                value={filterType}
                onChange={(e) => setFilterType(e?.target?.value)}
                options={[
                  { value: 'all', label: 'All Types' },
                  { value: 'critical', label: 'Critical' },
                  { value: 'expiring-7', label: 'Expiring (7 days)' },
                  { value: 'expiring-14', label: 'Expiring (14 days)' },
                  { value: 'missing', label: 'Missing Documents' },
                  { value: 'incidents', label: 'Incidents' },
                  { value: 'checklists', label: 'Checklists' },
                  { value: 'documents', label: 'Documents' }
                ]}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Filter by Priority</label>
              <Select
                value={filterPriority}
                onChange={(e) => setFilterPriority(e?.target?.value)}
                options={[
                  { value: 'all', label: 'All Priorities' },
                  { value: 'critical', label: 'Critical' },
                  { value: 'high', label: 'High' },
                  { value: 'medium', label: 'Medium' },
                  { value: 'low', label: 'Low' }
                ]}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Filter by Status</label>
              <Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e?.target?.value)}
                options={[
                  { value: 'all', label: 'All Status' },
                  { value: 'unread', label: 'Unread' },
                  { value: 'read', label: 'Read' }
                ]}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Search</label>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                placeholder="Search notifications..."
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
              />
            </div>
          </div>
          {(filterType !== 'all' || filterPriority !== 'all' || filterStatus !== 'all' || searchQuery) && (
            <div className="mt-4">
              <Button variant="ghost" size="sm" iconName="X" onClick={handleClearFilters}>
                Clear Filters
              </Button>
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Icon name="Loader2" size={48} className="text-accent animate-spin" />
          </div>
        ) : filteredNotifications?.length === 0 ? (
          <div className="bg-card rounded-lg shadow-elevation-2 border border-border p-8 text-center">
            <Icon name="Bell" size={64} className="text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-foreground mb-2">No notifications found</h3>
            <p className="text-muted-foreground">Try adjusting your filters or check back later</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredNotifications?.map((notification) => (
              <NotificationCard
                key={notification?.id}
                notification={notification}
                onClick={() => handleNotificationClick(notification)}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default NotificationsCenter;